/**
 * Straight-line distance between points.
 *
 * Used where a route has no measured mileage yet: the planning screens, the weekly plan snapshot
 * and the Samsara sync's fallback all estimate a route from its stops this way, from this one copy
 * of the formula. Pure and dependency-free so both runtimes can import it.
 */

/** 1 metre in miles. */
export const MILES_PER_METER = 0.000621371

/** Great-circle distance between two coordinates, in metres. */
export function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000 // Earth's radius in meters
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLon = ((lon2 - lon1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

export interface PathPoint {
  lat?: number | null
  lng?: number | null
}

/**
 * Length of a path through consecutive points, in metres.
 *
 * A leg with a missing coordinate on either end is skipped rather than failing the whole path.
 * Null when there are fewer than two points or no leg could be measured.
 */
export function pathLengthMeters(points: PathPoint[] | null | undefined): number | null {
  if (!points || points.length < 2) return null
  let total = 0
  for (let i = 0; i < points.length - 1; i++) {
    const cur = points[i]
    const nxt = points[i + 1]
    if (cur?.lat && cur?.lng && nxt?.lat && nxt?.lng) {
      total += haversineDistance(cur.lat, cur.lng, nxt.lat, nxt.lng)
    }
  }
  return total > 0 ? total : null
}

/** Length of a path through planned stops, in miles to two decimal places. */
export function calculateDistanceFromPlannedStops(
  stops: PathPoint[] | null | undefined,
): number | null {
  const meters = pathLengthMeters(stops)
  return meters == null ? null : Math.round(meters * MILES_PER_METER * 100) / 100
}

/**
 * Apply the global planned-mileage percentage to a distance (e.g. crow-fly).
 * Use this when the distance comes from planned stops, not from Samsara/actual.
 */
export function applyPlannedMileageMultiplier(
  distanceMiles: number | null | undefined,
  config?: { planned_mileage_percentage_add?: number | null } | null,
): number | null {
  if (distanceMiles == null || distanceMiles <= 0) return distanceMiles ?? null
  const pct = config?.planned_mileage_percentage_add
  if (pct == null || pct <= 0) return distanceMiles
  const multiplier = 1 + pct / 100
  return Math.round(distanceMiles * multiplier * 100) / 100
}
